import React from "react";
import logoMombasa from "../../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../../core/img/branch-logos/logo_texastrack.png";

// Single, reusable printable-document template shared by LPO/Stock-In/
// Stock-Out/Stock Return. Modeled directly on Working_Files/texas_LPO_modal.pdf
// (company header, party block, Sl No/Qty/Product Name/Price/VAT/Amount
// table with a "Grand Total in KSHS" footer row, a Delivery/Remarks blank-
// line section next to a boxed "quote LPO number" instruction, a supplier-
// identity line above signatures, totals, Prepared By/Approved By, footer
// address) - that PDF is the only real sample we have and this template
// matches it element-for-element for LPO. Stock-In/Out/Return reuse the same
// core layout (header/party/table/totals/signatures/footer-address) with
// showVat=false, an adjusted title, and showLpoFooterBlock=false (the
// delivery/quote-box/supplier-identity section is only confirmed for LPO)
// until real samples for those three arrive.
//
// This component renders identically whether shown live (the in-app Preview
// modal) or converted to a static HTML string for the print popup
// (StockDocumentPrintModal uses ReactDOMServer.renderToStaticMarkup on this
// same component) - one template, two render paths, never duplicated.
const money = (n) => (Number(n) || 0).toFixed(2);

// Branch-specific letterhead logo, same 4 assets + MOMBASA-default fallback
// as Flutter's quotation_pdf_builder.dart `_logoAssetFor` and this app's own
// listExportUtils.js - keeps a printed LPO/Stock document consistent with
// the sales quotation PDF the same branch already produces.
const LOGO_BY_BRANCH_CODE = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};

// The print popup is opened via window.open("", "_blank") with no real URL
// (about:blank), so a webpack-emitted root-relative logo path (e.g.
// "/static/media/...jpg") would fail to resolve there even though it works
// fine in the live app - always resolve to a full absolute URL up front so
// both render paths (live preview and the static-markup popup) work
// identically.
const logoUrlFor = (branchCode) =>
  new URL(LOGO_BY_BRANCH_CODE[branchCode] || logoMombasa, window.location.origin).href;

// A clean, print-safe system-font stack (no external @font-face - the print
// popup is a bare window.open() with no time to load a webfont before
// window.print() fires) rather than plain Arial, which read as generic/
// plain next to the reference sample (Working_Files/texas_LPO_modal.pdf).
const FONT_FAMILY = '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const cell = { border: "1px solid #000", padding: 4 };
// Line-item table only, slightly denser than the document's own 11px base -
// matches the reference sample's compact row height.
const itemCell = { ...cell, fontSize: 10.5 };

// Blank filler rows pad the item table out to a consistent height so a
// short LPO still fills a proper A4 page (matching the reference PDF's own
// look, which has trailing blank rows) instead of leaving a document that's
// mostly empty space above the totals box. Once the real item count already
// reaches this many rows there's nothing to pad - the table is left to flow
// and paginate naturally across as many pages as it needs.
//
// 12 -> 10 (2026-08-25): the OFFICE COPY page carries two extra lines
// (Purpose/Purpose Description, isOfficeCopy-only, see below) that the
// SUPPLIER COPY page doesn't - with 12 filler rows that pushed just the
// trailing footer-address box onto a lone 3rd page. 10 rows reclaims
// enough headroom for both copies to fit on their own single A4 page.
const FILLER_TARGET_ROWS = 8;

const StockDocumentPreview = ({
  title,
  docNo,
  docDate,
  refNo = "",
  // Branch-aware as of Batch85_CompanyInfoBranchAware.sql (GET
  // /settings/company-info?branchId=) - these defaults are a last-resort
  // fallback only (settings still loading, or genuinely never configured
  // for this branch or globally), not the real letterhead source anymore.
  companyName = "TEXAS ALARMS (K) LTD",
  companyAddress,
  companyPhone,
  companyEmail,
  branchLabel,
  // When set, replaces the LPO-style right-aligned-logo/title header with a
  // centered-logo + yellow tagline banner (matching the real customer-
  // facing quotation letterhead sample) - a short, single-line join of a
  // few of the branch's own Service Type master entries, resolved by the
  // caller (buildQuotationPrintProps.js), not this component.
  letterheadBannerText,
  // Quotation only - single centered/underlined heading shown right under
  // the yellow banner. Caller (buildQuotationPrintProps.js) already decides
  // the wording (the linked Sales Enquiry's service types, or a "Quotation
  // from {company}" fallback when there's no enquiry link) - this component
  // just renders whatever string it's given.
  quotationSubject,
  partyLabel = "Party Name",
  partyName,
  partyAddress,
  partyMobile,
  remarks,
  // LPO only - drives the "CASH LPO"/"CREDIT LPO" sub-heading under the
  // title. Undefined for Stock-In/Out/Return, so that line just doesn't render.
  paymentType,
  // LPO only - shown in the header block, but only on the OFFICE COPY (see
  // copyLabel below), per direct instruction that these stay internal.
  purposeTypeLabel,
  purposeDescription,
  // LPO only, raw (not the formatted purposeTypeLabel) - gates the Customer
  // line below, which only ever has a value for a CUSTOMER_SPECIFIC LPO.
  purposeType,
  customerName,
  showVat = false,
  vatPercent = 0,
  // Stock-In / Stock-Out (2026-09-14, "show vat and exact calculations"):
  // every line prints Price, Amount = Qty x Price, VAT %, VAT and Total =
  // Amount + VAT, the footer row totals each money column, and the box below
  // repeats Sub Total + VAT = Total Inc VAT. Lines then carry vatPercent and
  // lineTotal too. Off for LPO/Quotation, whose layout is unchanged.
  showVatDetail = false,
  // Stock-Out uses "Last Purchase Cost" here instead of "Price" - the value
  // shown is what was last paid for the product (see LastPurchasePriceButton
  // in LineItemsEditor.jsx), not a price charged to the recipient, and the
  // generic "Price" label was reading as confusing/misleading there.
  priceColumnLabel = "Price",
  lines = [],
  totals = { subTotal: 0, vatAmount: 0, netAmount: 0 },
  preparedByName,
  approvedByName,
  // Derived purely from the document's own ApprovedBy -> App_Users ->
  // App_Designations join (no new stored column on any document table) -
  // shown alongside Approved By, e.g. "SAJEESH KUMAR (Technical Manager)".
  approvedByDesignation,
  // Formatted date+time string (caller's responsibility - this component
  // doesn't know the raw ISO value), shown alongside Approved By so the
  // print doesn't just say who approved it, it says when. Blank when the
  // document isn't approved yet, same as approvedByName.
  approvedDate,
  // Optional sanitized HTML block (same caller-sanitizes contract as
  // footerNotesHtml below) rendered after the party block and before the
  // line-item table, with a fixed "Dear {partyName}," greeting line ahead
  // of it - the user only ever types the body ("Thank you for reaching
  // out..."), never the greeting itself. Undefined for LPO/Stock-In/Out/
  // Return, so none of them render anything new here.
  coverLetterHtml,
  // Optional sanitized HTML block (caller's responsibility to sanitize,
  // e.g. via DOMPurify - this component trusts what it's given) rendered
  // after the totals box and before the signature block - a Quotation's
  // free-form "Footer Notes" (payment terms/validity/warranty text, a
  // WYSIWYG-authored rich-text field). Undefined for every existing LPO/
  // Stock-In/Out/Return caller, so none of them render anything new here.
  footerNotesHtml,
  // The approver's own pre-uploaded seal/signature image (Batch92, an
  // "Approval Signature" Employee Document), already resolved by the
  // caller to a data: URI - blank until the LPO is actually approved,
  // same as approvedByName/approvedDate.
  signatureImageUrl,
  // Only confirmed against the real LPO sample - see the file-level comment.
  showLpoFooterBlock = false,
  copyLabel,
}) => {
  const totalQty = lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
  const fillerCount = Math.max(0, FILLER_TARGET_ROWS - lines.length);
  const isOfficeCopy = copyLabel === "OFFICE COPY";
  // Built from whatever's actually configured (global or branch-overridden)
  // rather than a hardcoded Mombasa address - a branch with nothing set for
  // a given field just omits that part instead of printing a blank/null.
  const footerLine = [companyAddress, companyPhone && `TEL:${companyPhone}`, companyEmail && `EMAIL: ${companyEmail}`]
    .filter(Boolean)
    .join(", ");

  return (
    <div style={{ fontFamily: FONT_FAMILY, fontSize: 11, lineHeight: 1.2, color: "#000", background: "#fff", padding: 16, position: "relative" }}>
      {copyLabel && (
        <div
          style={{
            position: "absolute",
            top: "40%",
            left: "10%",
            fontSize: 38,
            fontWeight: "bold",
            // Lowered from 0.12 (handoff §27) - noticeably more transparent
            // so the watermark doesn't compete with the printed content.
            color: "rgba(0,0,0,0.06)",
            transform: "rotate(-30deg)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {copyLabel}
        </div>
      )}

      {letterheadBannerText ? (
        // Centered logo + yellow tagline banner, matching the real
        // reference sample (a customer-facing quotation letterhead) rather
        // than LPO's own right-aligned-logo/title header - Quotation is
        // the only caller that passes this prop.
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <img src={logoUrlFor(branchLabel)} alt={companyName} style={{ height: 110 }} />
          <div style={{ marginTop: 10, background: "#FFF200", padding: "8px 12px" }}>
            <div style={{ fontSize: 10, fontWeight: "bold", fontStyle: "italic" }}>{letterheadBannerText}</div>
          </div>
          {quotationSubject && (
            <div style={{ fontSize: 13, fontWeight: 700, textDecoration: "underline", marginTop: 10 }}>
              {quotationSubject}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.5 }}>{title}</div>
            {paymentType && (
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3, letterSpacing: 0.5 }}>
                {paymentType === "CREDIT" ? "CREDIT LPO" : "CASH LPO"}
              </div>
            )}
          </div>
          <div style={{ flex: "0 0 auto", marginLeft: 12 }}>
            <img src={logoUrlFor(branchLabel)} alt={companyName} style={{ height: 75 }} />
          </div>
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ ...cell, padding: 8, verticalAlign: "top", width: "55%" }}>
              <div>{partyLabel}: &nbsp; {partyName || "-"}</div>
              <div style={{ marginTop: 8 }}>Address: &nbsp; {partyAddress || ""}</div>
              <div style={{ marginTop: 14 }}>Mobile No: &nbsp; {partyMobile || ""}</div>
            </td>
            <td style={{ ...cell, padding: 8, verticalAlign: "top" }}>
              <div>No: <strong>{docNo}</strong></div>
              <div>Date: &nbsp; {docDate}</div>
              {refNo && <div style={{ marginTop: 8 }}>Reference No: &nbsp; {refNo}</div>}
              {remarks && (
                <>
                  <div style={{ marginTop: 8 }}>Remarks</div>
                  <div style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{remarks}</div>
                </>
              )}
              {isOfficeCopy && purposeTypeLabel && (
                <div style={{ marginTop: 6 }}>Purpose: &nbsp; {purposeTypeLabel}</div>
              )}
              {isOfficeCopy && purposeDescription && (
                <div style={{ marginTop: 3, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                  Purpose Description: &nbsp; {purposeDescription}
                </div>
              )}
              {isOfficeCopy && purposeType === "CUSTOMER_SPECIFIC" && customerName && (
                <div style={{ marginTop: 3, wordBreak: "break-word" }}>Customer: &nbsp; {customerName}</div>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {coverLetterHtml && (
        <div style={{ marginBottom: 10, fontSize: 10.5, pageBreakInside: "avoid", breakInside: "avoid" }}>
          <div style={{ marginBottom: 6 }}>Dear {partyName || "Sir/Madam"},</div>
          <div dangerouslySetInnerHTML={{ __html: coverLetterHtml }} />
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", marginBottom: 8 }}>
        <thead style={{ display: "table-header-group" }}>
          <tr>
            <th style={{ ...itemCell, width: 36 }}>Sl No</th>
            <th style={{ ...itemCell, width: 70 }}>Quantity</th>
            <th style={{ ...itemCell, textAlign: "left" }}>Product Name</th>
            <th style={{ ...itemCell, width: 90 }}>{priceColumnLabel}</th>
            {showVatDetail ? (
              <>
                <th style={{ ...itemCell, width: 90 }}>Amount</th>
                <th style={{ ...itemCell, width: 48 }}>VAT %</th>
                <th style={{ ...itemCell, width: 80 }}>VAT</th>
                <th style={{ ...itemCell, width: 95 }}>Total</th>
              </>
            ) : (
              <>
                {showVat && <th style={{ ...itemCell, width: 90 }}>VAT</th>}
                <th style={{ ...itemCell, width: 100 }}>Amount</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {lines.map((line, idx) => (
            <tr key={idx} style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <td style={{ ...itemCell, textAlign: "center" }}>{idx + 1}</td>
              <td style={{ ...itemCell, textAlign: "center" }}>{money(line.qty)}</td>
              <td style={itemCell}>{line.productName}</td>
              <td style={{ ...itemCell, textAlign: "right" }}>{line.price == null ? "-" : money(line.price)}</td>
              {showVatDetail ? (
                <>
                  <td style={{ ...itemCell, textAlign: "right" }}>{line.amount == null ? "-" : money(line.amount)}</td>
                  <td style={{ ...itemCell, textAlign: "center" }}>{line.vatPercent == null ? "-" : `${Number(line.vatPercent)}%`}</td>
                  <td style={{ ...itemCell, textAlign: "right" }}>{line.vatAmount == null ? "-" : money(line.vatAmount)}</td>
                  <td style={{ ...itemCell, textAlign: "right" }}>{line.lineTotal == null ? "-" : money(line.lineTotal)}</td>
                </>
              ) : (
                <>
                  {showVat && <td style={{ ...itemCell, textAlign: "right" }}>{money(line.vatAmount)}</td>}
                  <td style={{ ...itemCell, textAlign: "right" }}>{money(line.amount)}</td>
                </>
              )}
            </tr>
          ))}
          {Array.from({ length: fillerCount }).map((_, idx) => (
            <tr key={`filler-${idx}`} style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <td style={itemCell}>&nbsp;</td>
              <td style={itemCell}>&nbsp;</td>
              <td style={itemCell}>&nbsp;</td>
              <td style={itemCell}>&nbsp;</td>
              {showVatDetail ? (
                <>
                  <td style={itemCell}>&nbsp;</td>
                  <td style={itemCell}>&nbsp;</td>
                  <td style={itemCell}>&nbsp;</td>
                </>
              ) : (
                showVat && <td style={itemCell}>&nbsp;</td>
              )}
              <td style={itemCell}>&nbsp;</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
            <td style={itemCell} />
            <td style={{ ...itemCell, textAlign: "center" }}>{money(totalQty)}</td>
            {showVatDetail ? (
              <>
                <td style={{ ...itemCell, fontWeight: "bold", textAlign: "center" }} colSpan={2}>
                  Totals in KSHS
                </td>
                <td style={{ ...itemCell, textAlign: "right", fontWeight: "bold" }}>{money(totals.subTotal)}</td>
                <td style={itemCell} />
                <td style={{ ...itemCell, textAlign: "right", fontWeight: "bold" }}>{money(totals.vatAmount)}</td>
                <td style={{ ...itemCell, textAlign: "right", fontWeight: "bold" }}>{money(totals.netAmount)}</td>
              </>
            ) : (
              <>
                <td style={{ ...itemCell, fontWeight: "bold", textAlign: "center" }} colSpan={showVat ? 3 : 2}>
                  Grand Total in KSHS
                </td>
                <td style={{ ...itemCell, textAlign: "right", fontWeight: "bold" }}>{money(totals.netAmount)}</td>
              </>
            )}
          </tr>
        </tfoot>
      </table>

      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
        <tbody>
          <tr>
            {/* Footer notes sit here, beside the totals box, not under it
                (2026-09-19) - under it they left the whole left side of this
                row blank and pushed the notes down the page. The scoped rules
                mirror the Quill editor the notes are typed in (no paragraph
                margins, 1.5em list indent), so the print reads the way it was
                typed; an inline <style> because the print popup is static
                markup with no app stylesheet. */}
            <td style={{ width: "60%", verticalAlign: "top", paddingRight: 14 }}>
              {footerNotesHtml && (
                <>
                  <style>{`.doc-footer-notes p{margin:0}.doc-footer-notes ol,.doc-footer-notes ul{margin:0;padding-left:1.5em}.doc-footer-notes li{margin:0}`}</style>
                  <div
                    className="doc-footer-notes"
                    style={{ fontSize: 12, lineHeight: 1.42 }}
                    dangerouslySetInnerHTML={{ __html: footerNotesHtml }}
                  />
                </>
              )}
            </td>
            <td style={{ verticalAlign: "top" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000" }}>
                <tbody>
                  {(showVat || showVatDetail) && (
                    <>
                      <tr>
                        <td style={cell}>{showVatDetail ? "Sub Total (ex VAT)" : "Sub Total"}</td>
                        <td style={{ ...cell, textAlign: "right" }}>{money(totals.subTotal)}</td>
                      </tr>
                      {/* Quotation discount (Batch163) - one figure off the
                          line total, VAT charged on the net. Only rendered when
                          there is one, so no other document changes. */}
                      {totals.discountAmount > 0 && (
                        <>
                          <tr>
                            <td style={cell}>{totals.discountLabel || "Discount"}</td>
                            <td style={{ ...cell, textAlign: "right" }}>-{money(totals.discountAmount)}</td>
                          </tr>
                          <tr>
                            <td style={cell}>Net</td>
                            <td style={{ ...cell, textAlign: "right" }}>{money(totals.subTotal - totals.discountAmount)}</td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td style={cell}>{vatPercent == null ? "VAT" : `VAT ${vatPercent}%`}</td>
                        <td style={{ ...cell, textAlign: "right" }}>{money(totals.vatAmount)}</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td style={{ ...cell, fontWeight: "bold" }}>
                      {showVat || showVatDetail ? "Total Inc VAT" : "Grand Total"}
                    </td>
                    <td style={{ ...cell, textAlign: "right", fontWeight: "bold" }}>
                      {money(totals.netAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      {showLpoFooterBlock && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8, pageBreakInside: "avoid", breakInside: "avoid" }}>
          <tbody>
            <tr>
              <td style={{ verticalAlign: "top", width: "60%", paddingRight: 16 }}>
                <div style={{ marginBottom: 12 }}>
                  DELIVERY TO OUR OFFICE /DEPOT AT&nbsp;
                  <span style={{ display: "inline-block", borderBottom: "1px solid #000", width: 260 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ display: "inline-block", borderBottom: "1px solid #000", width: 380 }} />
                </div>
                <div>
                  REMARKS IF ANY&nbsp;
                  <span style={{ display: "inline-block", borderBottom: "1px solid #000", width: 300 }} />
                </div>
              </td>
              <td style={{ verticalAlign: "top" }}>
                <div style={{ border: "1px solid #000", padding: 8, textAlign: "center", fontWeight: "bold" }}>
                  PLEASE QUOTE LPO NUMBER
                  <br />
                  ON YOUR INVOICE, PAYMENT
                  <br />
                  CAN ONLY BE MADE AGAINST
                  <br />
                  OFFICIAL ORDER
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {showLpoFooterBlock && (
        <div style={{ borderTop: "2px solid #000", borderBottom: "1px solid #000", padding: "8px 0", marginBottom: 8, pageBreakInside: "avoid", breakInside: "avoid" }}>
          This Supplier must indicate the identity of {companyName} Officer who
          collects the goods by showing on the Delivery Note Or Invoice
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10, pageBreakInside: "avoid", breakInside: "avoid" }}>
        <tbody>
          <tr>
            <td style={{ paddingTop: showLpoFooterBlock ? 0 : 24 }}>
              {showLpoFooterBlock && <>Date: &nbsp; _______________________<br /></>}
              Prepared By: &nbsp; {preparedByName || "_______________________"}
              <br />
              Approved By: &nbsp; {approvedByName || "_______________________"}
              {approvedByName && approvedByDesignation && <span> &nbsp;({approvedByDesignation})</span>}
              {approvedByName && approvedDate && <span> &nbsp;on&nbsp; {approvedDate}</span>}
            </td>
            <td style={{ textAlign: "right", verticalAlign: "bottom", position: "relative" }}>
              {signatureImageUrl && (
                // Whatever's actually uploaded varies wildly in raw
                // dimensions/orientation (a phone photo of a whole stamped
                // page vs. a tightly-cropped image) - a bare height
                // constraint with no width shrinks a tall/portrait source
                // down to a sliver (the original bug). object-fit:cover in
                // a fixed landscape box instead always fills the box,
                // center-cropping whatever doesn't fit - for a photographed
                // page the stamp is usually roughly centered in frame, so
                // this reads as a normal-sized stamp instead of a distant
                // thumbnail. The negative margin lets it sit large and
                // overlap down onto "For and on behalf of" underneath, the
                // way a real ink stamp physically overlaps a signature
                // line on paper - deliberate, not a layout bug.
                <div style={{ width: 260, height: 120, marginLeft: "auto", marginBottom: -28, overflow: "hidden", position: "relative", zIndex: 2 }}>
                  <img
                    src={signatureImageUrl}
                    alt="Approval signature"
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
                  />
                </div>
              )}
              <div style={{ position: "relative", zIndex: 1 }}>
                For and on behalf of
                <br />
                <strong>{companyName}</strong>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ border: "1px solid #000", padding: 8, textAlign: "center", fontSize: 11, pageBreakInside: "avoid", breakInside: "avoid" }}>
        {showLpoFooterBlock && <div style={{ marginBottom: 4 }}>TO AVOID DELAY IN PAYMENTS PLEASE SUBMIT YOUR INVOICE TO</div>}
        {showLpoFooterBlock && <div style={{ fontWeight: "bold", marginBottom: 4 }}>{companyName}</div>}
        {footerLine && <div>{footerLine}</div>}
      </div>
    </div>
  );
};

export default StockDocumentPreview;
